import { DragDropContext, type DropResult } from "@hello-pangea/dnd";
import { Plus } from "lucide-react";
import {
  useDataProvider,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useCallback, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";

import type {
  PipelineCardSummary,
  PipelineStage,
  Sale,
  Tag,
} from "../../types";
import type { PendingMove } from "../pendingMoves";
import { getDropPosition } from "../positions";
import { useRefreshBoard } from "../useRefreshBoard";
import { DeleteStageDialog } from "./DeleteStageDialog";
import { StageColumn } from "./StageColumn";
import { StageNameDialog } from "./StageNameDialog";
import { useStageCardCount } from "./usePipelineData";
import { useStageActions } from "./useStageActions";

export interface PipelineBoardProps {
  pipelineId: Identifier;
  stages: PipelineStage[];
  /** Server filter of the cards (pipeline and board filters). */
  filter: Record<string, unknown>;
  tagsById: Map<number, Tag>;
  salesById: Map<Sale["id"], Sale>;
  onAddCard: (stageId: Identifier) => void;
  onOpenCard: (card: PipelineCardSummary) => void;
}

type AddStageState =
  | { open: false }
  | { open: true; after: PipelineStage | null };

/**
 * Pipeline board. Each stage paginates on its own (25 cards at a time);
 * dragging changes the stage and the order.
 */
export const PipelineBoard = ({
  pipelineId,
  stages,
  filter,
  tagsById,
  salesById,
  onAddCard,
  onOpenCard,
}: PipelineBoardProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refreshBoard = useRefreshBoard();
  const stageActions = useStageActions(pipelineId, stages);
  const columnsRef = useRef(new Map<string, PipelineCardSummary[]>());
  // Moves shown immediately, until the refetched data contains them
  const [pendingMoves, setPendingMoves] = useState<
    Record<string, PendingMove<PipelineCardSummary>>
  >({});
  const [addStage, setAddStage] = useState<AddStageState>({ open: false });
  const [stageToDelete, setStageToDelete] = useState<PipelineStage | null>(
    null,
  );

  // The dialog opens once the number of cards to move is known
  const stageCardCount = useStageCardCount(stageToDelete?.id);

  const handleItemsChange = useCallback(
    (stageId: string, cards: PipelineCardSummary[]) => {
      columnsRef.current.set(stageId, cards);
    },
    [],
  );

  const otherStages = useMemo(
    () => stages.filter((stage) => stage.id !== stageToDelete?.id),
    [stages, stageToDelete],
  );

  const handleDragEnd = async ({
    draggableId,
    source,
    destination,
  }: DropResult) => {
    if (
      !destination ||
      (destination.droppableId === source.droppableId &&
        destination.index === source.index)
    ) {
      return;
    }
    const card = columnsRef.current
      .get(source.droppableId)
      ?.find((candidate) => String(candidate.id) === draggableId);
    const stage = stages.find(
      (candidate) => String(candidate.id) === destination.droppableId,
    );
    if (!card || !stage) return;

    const position = getDropPosition(
      columnsRef.current.get(destination.droppableId) ?? [],
      card.id,
      destination.index,
    );
    setPendingMoves((moves) => ({
      ...moves,
      [draggableId]: {
        item: card,
        columnId: destination.droppableId,
        position,
      },
    }));
    try {
      await dataProvider.update("pipeline_cards", {
        id: card.id,
        data: { stage_id: stage.id, position },
        previousData: card,
      });
      await refreshBoard();
    } catch (error: unknown) {
      notify(
        error instanceof Error ? error.message : "ra.notification.http_error",
        { type: "error" },
      );
    } finally {
      setPendingMoves(({ [draggableId]: _done, ...moves }) => moves);
    }
  };

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex min-h-[60vh] gap-3 overflow-x-auto pb-4">
          {stages.map((stage, index) => (
            <StageColumn
              key={stage.id}
              stage={stage}
              filter={filter}
              pendingMoves={pendingMoves}
              onItemsChange={handleItemsChange}
              tagsById={tagsById}
              salesById={salesById}
              canMoveLeft={index > 0}
              canMoveRight={index < stages.length - 1}
              onRename={(name) => stageActions.rename(stage, name)}
              onMove={(direction) => stageActions.move(stage, direction)}
              onAddStageAfter={() => setAddStage({ open: true, after: stage })}
              onDelete={() => setStageToDelete(stage)}
              onAddCard={() => onAddCard(stage.id)}
              onOpenCard={onOpenCard}
            />
          ))}
          <Button
            variant="outline"
            className="h-12 w-60 shrink-0 border-dashed"
            onClick={() => setAddStage({ open: true, after: null })}
          >
            <Plus />
            {translate("crm.operations.stages.add")}
          </Button>
        </div>
      </DragDropContext>
      <StageNameDialog
        open={addStage.open}
        onClose={() => setAddStage({ open: false })}
        onSubmit={(name) =>
          stageActions.add(name, addStage.open ? addStage.after : null)
        }
      />
      <DeleteStageDialog
        stage={stageCardCount === undefined ? null : stageToDelete}
        cardCount={stageCardCount ?? 0}
        otherStages={otherStages}
        onClose={() => setStageToDelete(null)}
        onConfirm={(targetStageId) =>
          stageToDelete
            ? stageActions.remove(stageToDelete, targetStageId)
            : Promise.resolve()
        }
      />
    </>
  );
};
